import {
  BusinessModalName,
  BusinessModalNames,
  CallState,
  CorpBusinessAccount,
  CorpSessionInfo,
  ICorpPerson,
  IPerson,
  ModalDataMap,
  PersonId,
  PolicyTier,
  RewardsAccount,
  SessionInfo,
} from "redmond";
import { IUpdateUserPassengerRequest } from "../api/v1/user-passengers/updateUserPassenger";
import { IDeleteUserPassengerRequest } from "../api/v1/user-passengers/deleteUserPassenger";

export const SET_SESSION_INFO = "b2bBase/SET_SESSION_INFO";
export type SET_SESSION_INFO = typeof SET_SESSION_INFO;

export interface ISetSessionInfo {
  type: SET_SESSION_INFO;
  sessionInfo: SessionInfo | CorpSessionInfo;
}

export const setSessionInfoInStore = (
  sessionInfo: SessionInfo | CorpSessionInfo
): ISetSessionInfo => ({
  type: SET_SESSION_INFO,
  sessionInfo,
});

export const SET_MODAL_METADATA_INFO = "b2bBase/SET_MODAL_METADATA_INFO";
export type SET_MODAL_METADATA_INFO = typeof SET_MODAL_METADATA_INFO;

export interface ISetModalMetadataInfo {
  type: SET_MODAL_METADATA_INFO;
  hasSeenModalMap: Record<BusinessModalName, boolean>;
  metadata: { [K in BusinessModalNames]?: ModalDataMap[K] };
}

export const setModalMetadataInfoInStore = (
  hasSeenModalMap: Record<BusinessModalName, boolean>,
  metadata: { [K in BusinessModalNames]?: ModalDataMap[K] }
): ISetModalMetadataInfo => ({
  type: SET_MODAL_METADATA_INFO,
  hasSeenModalMap,
  metadata,
});

export const SET_POLICY_TIER = "b2bBase/SET_POLICY_TIER";
export type SET_POLICY_TIER = typeof SET_POLICY_TIER;

export interface ISetPolicyTier {
  type: SET_POLICY_TIER;
  policyTier?: PolicyTier;
}

export const setPolicyTier = (policyTier: PolicyTier): ISetPolicyTier => ({
  type: SET_POLICY_TIER,
  policyTier,
});

export const SET_BUSINESS_ACCOUNTS = "b2bBase/SET_BUSINESS_ACCOUNTS";
export type SET_BUSINESS_ACCOUNTS = typeof SET_BUSINESS_ACCOUNTS;

export interface ISetBusinessAccounts {
  type: SET_BUSINESS_ACCOUNTS;
  businessAccounts: CorpBusinessAccount[];
}

export const setBusinessAccounts = (
  businessAccounts: CorpBusinessAccount[]
): ISetBusinessAccounts => ({
  type: SET_BUSINESS_ACCOUNTS,
  businessAccounts,
});

export const SET_REWARDS_ACCOUNTS = "b2bBase/SET_REWARDS_ACCOUNTS";

export type SET_REWARDS_ACCOUNTS = typeof SET_REWARDS_ACCOUNTS;

export interface ISetRewardsAccounts {
  type: SET_REWARDS_ACCOUNTS;
  rewardsAccounts: RewardsAccount[];
}

export const setRewardsAccountsInStore = (
  rewardsAccounts: RewardsAccount[]
): ISetRewardsAccounts => ({
  type: SET_REWARDS_ACCOUNTS,
  rewardsAccounts,
});

export const SET_SELECTED_ACCOUNT_REFERENCE_ID =
  "b2bBase/SET_SELECTED_ACCOUNT_REFERENCE_ID";

export type SET_SELECTED_ACCOUNT_REFERENCE_ID =
  typeof SET_SELECTED_ACCOUNT_REFERENCE_ID;

export interface ISetSelectedAccountReferenceId {
  type: SET_SELECTED_ACCOUNT_REFERENCE_ID;
  selectedAccountReferenceId: string;
}

export const setSelectedAccountReferenceIdInStore = (
  selectedAccountReferenceId: string
): ISetSelectedAccountReferenceId => ({
  type: SET_SELECTED_ACCOUNT_REFERENCE_ID,
  selectedAccountReferenceId,
});

export const FETCH_CORP_USER_PASSENGERS = "b2bBase/FETCH_CORP_USER_PASSENGERS";

export type FETCH_CORP_USER_PASSENGERS = typeof FETCH_CORP_USER_PASSENGERS;

export interface IFetchCorpUserPassengers {
  type: FETCH_CORP_USER_PASSENGERS;
}

export const fetchCorpUserPassengers = (): IFetchCorpUserPassengers => ({
  type: FETCH_CORP_USER_PASSENGERS,
});

export const UPDATE_USER_PASSENGER = "b2bBase/UPDATE_USER_PASSENGER";
export type UPDATE_USER_PASSENGER = typeof UPDATE_USER_PASSENGER;

export const updateUserPassenger = (
  payload: IUpdateUserPassengerArgs,
  defaultTravelerId?: string,
  entryPoint?: string
): IUpdateUserPassenger => ({
  type: UPDATE_USER_PASSENGER,
  payload,
  defaultTravelerId,
  entryPoint,
});

export interface IUpdateUserPassenger {
  type: UPDATE_USER_PASSENGER;
  payload: IUpdateUserPassengerArgs;
  defaultTravelerId?: string;
  entryPoint?: string;
}

export interface IUpdateUserPassengerArgs extends IUpdateUserPassengerRequest {}

export const DELETE_USER_PASSENGER = "b2bBase/DELETE_USER_PASSENGER";
export type DELETE_USER_PASSENGER = typeof DELETE_USER_PASSENGER;

export const deleteUserPassenger = (
  payload: IDeleteUserPassengerArgs
): IDeleteUserPassenger => ({
  type: DELETE_USER_PASSENGER,
  payload,
});

export interface IDeleteUserPassenger {
  type: DELETE_USER_PASSENGER;
  payload: IDeleteUserPassengerArgs;
}

export interface IDeleteUserPassengerArgs extends IDeleteUserPassengerRequest {}

export const SET_USER_SELECTED_PASSENGER_IDS =
  "b2bBase/SET_USER_SELECTED_PASSENGER_IDS";
export type SET_USER_SELECTED_PASSENGER_IDS =
  typeof SET_USER_SELECTED_PASSENGER_IDS;

export const setUserSelectedPassengerIds = (args: {
  userSelectedPassengerIds: PersonId[];
}): ISetUserSelectedPassengerIds => ({
  type: SET_USER_SELECTED_PASSENGER_IDS,
  ...args,
});

export interface ISetUserSelectedPassengerIds {
  type: SET_USER_SELECTED_PASSENGER_IDS;
  userSelectedPassengerIds: PersonId[];
}

export const SET_USER_PASSENGERS_CALL_STATE_FAILED =
  "b2bBase/SET_USER_PASSENGERS_CALL_STATE_FAILED";
export type SET_USER_PASSENGERS_CALL_STATE_FAILED =
  typeof SET_USER_PASSENGERS_CALL_STATE_FAILED;

export const setUserPassengersCallStateFailed =
  (): ISetUserPassengersCallStateFailed => ({
    type: SET_USER_PASSENGERS_CALL_STATE_FAILED,
  });

export interface ISetUserPassengersCallStateFailed {
  type: SET_USER_PASSENGERS_CALL_STATE_FAILED;
}

export const SET_USER_PASSENGERS = "b2bBase/SET_USER_PASSENGERS";
export type SET_USER_PASSENGERS = typeof SET_USER_PASSENGERS;

export const setUserPassengers = (args: {
  userPassengerCallState: CallState;
  userPassengers: IPerson[] | ICorpPerson[];
}): ISetUserPassengers => ({
  type: SET_USER_PASSENGERS,
  ...args,
});

export interface ISetUserPassengers {
  type: SET_USER_PASSENGERS;
  userPassengerCallState: CallState;
  userPassengers: IPerson[] | ICorpPerson[];
}

export const SET_IS_MULTIPORTAL = "b2bBase/SET_IS_MULTIPORTAL";
export type SET_IS_MULTIPORTAL = typeof SET_IS_MULTIPORTAL;

export interface ISetIsMultiportal {
  type: SET_IS_MULTIPORTAL;
  isMultiportal: boolean;
}

export const setIsMultiportal = (isMultiportal: boolean): ISetIsMultiportal => ({
  type: SET_IS_MULTIPORTAL,
  isMultiportal,
});

export type B2bBaseActions =
  | ISetSessionInfo
  | ISetBusinessAccounts
  | ISetRewardsAccounts
  | ISetSelectedAccountReferenceId
  | ISetModalMetadataInfo
  | ISetPolicyTier
  | IFetchCorpUserPassengers
  | IDeleteUserPassenger
  | IUpdateUserPassenger
  | ISetUserSelectedPassengerIds
  | ISetUserPassengersCallStateFailed
  | ISetUserPassengers
  | ISetIsMultiportal;
